// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { useCostAggregate } from '@/costs/hooks/use-cost-aggregate';
import { useTimeRecordAggregate } from '@/costs/hooks/use-time-record-aggregate';
import { formatHours } from '@/costs/utilities/format-hours';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import { Box, Grid, Typography } from '@mui/material';

interface Props {
  projectId: string;
}

interface CurrencyEntryCounts {
  isoCode: IsoCurrencyCode;
  totalEntries: number;
  unconvertedEntries: number;
}

function getEntryBreakdown(
  buckets: CurrencyEntryCounts[],
  organizationCurrencyCode: IsoCurrencyCode | null,
  totalEntries: number,
  remainderLabel: string
) {
  // Explain which entries contribute to the total and which have no usable rate or amount.
  let bucketEntries = 0;
  let convertedEntries = 0;
  const breakdown = buckets.map((bucket) => {
    bucketEntries += bucket.totalEntries;
    const count = `${bucket.totalEntries} in ${bucket.isoCode}`;
    if (bucket.isoCode === organizationCurrencyCode) {
      return count;
    }

    const converted = bucket.totalEntries - bucket.unconvertedEntries;
    convertedEntries += converted;
    if (bucket.unconvertedEntries === 0) {
      return `${count}, converted`;
    }

    if (converted === 0) {
      return `${count}, not converted (no exchange rate available)`;
    }

    return `${count}: ${converted} converted, ${bucket.unconvertedEntries} not converted`;
  });
  if (totalEntries > bucketEntries) {
    breakdown.push(`${totalEntries - bucketEntries} ${remainderLabel}`);
  }

  const showBreakdown =
    breakdown.length > 1 ||
    totalEntries > bucketEntries ||
    buckets.some((bucket) => bucket.isoCode !== organizationCurrencyCode);
  return {
    convertedEntries,
    captionTooltip: showBreakdown ? (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {breakdown.map((line) => (
          <Typography component="li" variant="inherit" key={line}>
            {line}
          </Typography>
        ))}
      </Box>
    ) : undefined,
  };
}

export function ProjectCostSummary(props: Props) {
  const { aggregateData, isLoading, error } = useCostAggregate({
    projectIds: [props.projectId],
  });
  const timeAggregate = useTimeRecordAggregate({
    projectIds: [props.projectId],
  });

  const timeCaption = timeAggregate.error
    ? "Couldn't load time"
    : pluralize(timeAggregate.aggregateData.totalEntries, {
        singular: 'entry',
        plural: 'entries',
      });
  const caption = error
    ? "Couldn't load costs"
    : pluralize(aggregateData.totalEntries, {
        singular: 'entry',
        plural: 'entries',
      });

  const organizationCurrencyCode =
    aggregateData.organizationCurrencyTotal?.isoCode ?? null;
  const timeOrganizationCurrencyCode =
    timeAggregate.aggregateData.organizationCurrencyTotal?.isoCode ?? null;
  const costBreakdown = getEntryBreakdown(
    aggregateData.byCurrency,
    organizationCurrencyCode,
    aggregateData.totalEntries,
    'with no amount'
  );
  const timeBreakdown = getEntryBreakdown(
    timeAggregate.aggregateData.byCurrency,
    timeOrganizationCurrencyCode,
    timeAggregate.aggregateData.totalEntries,
    'unrated'
  );
  const valueTooltip =
    !error &&
    organizationCurrencyCode !== null &&
    costBreakdown.convertedEntries > 0
      ? `Includes costs in other currencies, converted to ${organizationCurrencyCode}, your organization's currency, at approximately the exchange rate on each cost's date.` +
        (aggregateData.unconvertedEntries > 0
          ? ` Left out of the total: ${aggregateData.unconvertedEntries} not converted (no exchange rate available).`
          : '')
      : undefined;

  const timeLeftOut = [
    ...(timeAggregate.aggregateData.unconvertedEntries > 0
      ? [
          `${timeAggregate.aggregateData.unconvertedEntries} not converted (no exchange rate available)`,
        ]
      : []),
    ...(timeAggregate.aggregateData.unratedEntries > 0
      ? [`${timeAggregate.aggregateData.unratedEntries} unrated`]
      : []),
  ];
  const timeValueTooltip =
    !timeAggregate.error &&
    timeAggregate.aggregateData.organizationCurrencyTotal !== null &&
    timeOrganizationCurrencyCode !== null &&
    timeBreakdown.convertedEntries > 0
      ? `Includes time in other currencies, converted to ${timeOrganizationCurrencyCode}, your organization's currency, at approximately the exchange rate on each entry's date.` +
        (timeLeftOut.length > 0
          ? ` Left out of the total: ${timeLeftOut.join(', ')}.`
          : '')
      : undefined;

  return (
    <Grid container spacing={2} sx={{ mb: 1 }}>
      <Grid item xs={12} md={4}>
        <SummaryCard size="compact">
          <PrimaryMetric
            size="compact"
            label="Time costs"
            value={
              timeAggregate.error ||
              timeAggregate.aggregateData.organizationCurrencyTotal === null
                ? '—'
                : formatCurrencyValue(
                    timeAggregate.aggregateData.organizationCurrencyTotal
                  )
            }
            caption={timeCaption}
            captionTooltip={
              timeAggregate.error ? undefined : timeBreakdown.captionTooltip
            }
            valueTooltip={timeValueTooltip}
            isLoading={timeAggregate.isLoading}
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SummaryCard size="compact">
          <PrimaryMetric
            size="compact"
            label="Other costs"
            value={
              error || aggregateData.organizationCurrencyTotal === null
                ? '—'
                : formatCurrencyValue(aggregateData.organizationCurrencyTotal)
            }
            caption={caption}
            captionTooltip={error ? undefined : costBreakdown.captionTooltip}
            valueTooltip={valueTooltip}
            isLoading={isLoading}
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SummaryCard size="compact">
          <PrimaryMetric
            size="compact"
            label="Hours reported"
            value={
              timeAggregate.error
                ? '—'
                : formatHours(timeAggregate.aggregateData.totalMinutes)
            }
            isLoading={timeAggregate.isLoading}
          />
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
